import { useState, useEffect, useCallback, useRef } from 'react';
import P23Import from './P23Import';
import P24SevenStepsNew from './P24SevenStepsNew';
import P25AgentRoundtableNew from './P25AgentRoundtableNew';
import P26ClassroomInputNew from './P26ClassroomInputNew';
import P27VisualUnderstanding from './P27VisualUnderstandingNew';
import P28TextUnderstandingNew from './P28TextUnderstandingNew';
import P29AudioUnderstandingNew from './P29AudioUnderstandingNew';
import P30MultiModalNew from './P30MultiModalNew';
import P31KnowledgeBase from './P31KnowledgeBaseNew';
import P34ExpertModel from './P34ExpertModelNew';
import P35BackendCombined from './P35BackendCombined';
import P35BackendDomains from './P35BackendDomainsNew';
import P36TriView from './P36TriViewNew';
import P38QualityTiers from './P38QualityTiersNew';
import P39EvidenceChain from './P39EvidenceChainNew';
import P40CaseDesertRoad from './P40CaseDesertRoad';
import P41CaseHighSpeedRail from './P41CaseHighSpeedRail';
import P43CredibilityVerify from './P43CredibilityVerifyNew';
import P44Comparison from './P44ComparisonNew';
import P45WrapUp from './P45WrapUpNew';

const TOTAL_SLIDES = 20;

export default function Section3Carousel() {
  const [activeSlide, setActiveSlide] = useState(0);
  const [direction, setDirection] = useState<'next' | 'prev' | 'initial'>('initial');
  const containerRef = useRef<HTMLDivElement>(null);

  const goNext = useCallback(() => {
    setDirection('next');
    setActiveSlide((prev) => (prev < TOTAL_SLIDES - 1 ? prev + 1 : prev));
  }, []);

  const goPrev = useCallback(() => {
    setDirection('prev');
    setActiveSlide((prev) => (prev > 0 ? prev - 1 : prev));
  }, []);

  const goTo = useCallback((i: number) => {
    setDirection(i > activeSlide ? 'next' : i < activeSlide ? 'prev' : 'initial');
    setActiveSlide(i);
  }, [activeSlide]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        goNext();
      }
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        goPrev();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [goNext, goPrev]);

  const slides = [
    <P23Import forceVisible key="p23" />,
    <P31KnowledgeBase forceVisible key="p31" />,
    <P24SevenStepsNew forceVisible key="p24" />,
    <P25AgentRoundtableNew forceVisible key="p25" />,
    <P26ClassroomInputNew forceVisible key="p26" />,
    <P27VisualUnderstanding forceVisible key="p27" />,
    <P28TextUnderstandingNew forceVisible key="p28" />,
    <P29AudioUnderstandingNew forceVisible key="p29" />,
    <P30MultiModalNew forceVisible key="p30" />,
    <P34ExpertModel forceVisible key="p34" />,
    <P35BackendCombined forceVisible key="p35" />,
    <P35BackendDomains forceVisible key="p35d" />,
    <P36TriView forceVisible key="p36" />,
    <P38QualityTiers forceVisible key="p38" />,
    <P39EvidenceChain forceVisible key="p39" />,
    <P40CaseDesertRoad forceVisible key="p40" />,
    <P41CaseHighSpeedRail forceVisible key="p41" />,
    <P43CredibilityVerify forceVisible key="p43" />,
    <P44Comparison forceVisible key="p44" />,
    <P45WrapUp forceVisible key="p45" />,
  ];

  return (
    <section
      id="section-3"
      ref={containerRef}
      className="relative min-h-screen overflow-hidden page-glow"
    >
      {/* 明显可见的三层浮动光斑 — 暖金色基调 */}
      <div className="absolute top-[10%] left-[5%] w-[420px] h-[420px] rounded-full bg-accent-200/30 blur-[100px] pointer-events-none animate-orb-float-3" />
      <div className="absolute bottom-[15%] right-[8%] w-[380px] h-[380px] rounded-full bg-accent-100/25 blur-[90px] pointer-events-none animate-orb-float-1" />
      <div className="absolute top-[45%] left-[55%] w-[320px] h-[320px] rounded-full bg-primary-100/15 blur-[110px] pointer-events-none animate-orb-float-2" />

      {slides.map((slide, i) => (
        <div
          key={i}
          className={`absolute inset-0 overflow-y-auto pb-20 transition-all duration-600 ${
            activeSlide === i
              ? direction === 'next'
                ? 'animate-slide-in-right z-10 pointer-events-auto'
                : direction === 'prev'
                  ? 'animate-slide-in-left z-10 pointer-events-auto'
                  : 'animate-slide-initial z-10 pointer-events-auto'
              : 'opacity-0 z-0 pointer-events-none scale-[0.98]'
          }`}
        >
          {slide}
        </div>
      ))}

      <button
        onClick={goPrev}
        disabled={activeSlide === 0}
        className={`absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-30 w-11 h-11 md:w-12 md:h-12 rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer ${
          activeSlide === 0
            ? 'glass border-white/40 text-foreground-950 cursor-default'
            : 'glass border-white/60 text-accent-500 hover:border-accent-300/60 hover:scale-110 active:scale-95'
        }`}
      >
        <i className="ri-arrow-left-s-line text-xl md:text-2xl" />
      </button>

      <button
        onClick={goNext}
        disabled={activeSlide === TOTAL_SLIDES - 1}
        className={`absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-30 w-11 h-11 md:w-12 md:h-12 rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer ${
          activeSlide === TOTAL_SLIDES - 1
            ? 'glass border-white/40 text-foreground-950 cursor-default'
            : 'glass border-white/60 text-accent-500 hover:border-accent-300/60 hover:scale-110 active:scale-95'
        }`}
      >
        <i className="ri-arrow-right-s-line text-xl md:text-2xl" />
      </button>

      <div className="absolute bottom-6 md:bottom-8 left-1/2 -translate-x-1/2 z-30 flex items-center gap-1 flex-wrap justify-center px-4">
        {Array.from({ length: TOTAL_SLIDES }).map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className={`rounded-full transition-all duration-300 cursor-pointer ${
              activeSlide === i
                ? 'w-6 h-1.5 bg-accent-500 rounded-full'
                : 'w-1.5 h-1.5 bg-background-300/50 hover:bg-accent-300/60 hover:scale-125'
            }`}
          />
        ))}
      </div>

      <div className="absolute bottom-6 md:bottom-8 right-6 md:right-10 z-30">
        <span className="font-mono text-xs text-foreground-950 tracking-wider">
          {activeSlide + 1} / {TOTAL_SLIDES}
        </span>
      </div>
    </section>
  );
}